import { WorkspaceAction, MspWorkspaceAction } from "@/constants/workplaces";
import { RouteName } from "@/constants/routes";
import { navigateToRoute } from "@/_helpers/navigation";
import { useMyAccountStore } from "@/_store/my-account.module";
import { useAccountStore } from "@/_store/account.module";
import { useDialogsStore } from "@/_store/dialogs.module";
import { MfaStatus } from "@/constants/account";
import type { BaseMspWorkspaceListItem } from "@/_store/msp/msp.module.ts";
import { isMspActionRestricted } from "@/_helpers/msp-permissions";
import { isGlobalActionRestricted } from "@/_helpers/global-permissions";
import type { useMspStore } from "@/_store/msp/msp.module.ts";
import type { useGlobalWorkspacesStore } from "@/_store/global-workspaces.module.ts";
import type { useMspTreeviewStore } from "@/_store/msp/msp-treeview.module.ts";
import { confirmationDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import {
  GlobalRoleScopes,
  GlobalRoleScopeSection,
  MspPortalScopeSection,
  MspRolePermissionScopes,
} from "@/constants/roles.ts";

export type WorkspaceActionsStore =
  | ReturnType<typeof useMspStore>
  | ReturnType<typeof useGlobalWorkspacesStore>
  | ReturnType<typeof useMspTreeviewStore>;

/**
 * Builds disabled state map for workspace actions in single hierarchy context based on MSP permissions.
 * @returns Record of actions mapped to disabled flags
 */
export const buildSingleHierarchyDisabledMap = (): Record<MspWorkspaceAction, boolean> =>
  ({
    [MspWorkspaceAction.EDIT_MSP_WORKSPACE_LABEL]: isMspActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "editWorkspace"
    ),
    [MspWorkspaceAction.EDIT_WORKSPACE]: isMspActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "editWorkspace"
    ),
    [MspWorkspaceAction.EDIT_SUBSCRIPTION]: isMspActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "editSubscription"
    ),
    [MspWorkspaceAction.CONVERT]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "convertWorkspace"
    ),
    [MspWorkspaceAction.UNARCHIVE_WORKSPACE]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "restoreWorkspace"
    ),
    [MspWorkspaceAction.START_SUBSCRIPTION]: isMspActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "startSubscription"
    ),
    [MspWorkspaceAction.STOP_SUBSCRIPTION]: isMspActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "stopSubscription"
    ),
    [MspWorkspaceAction.ARCHIVE_WORKSPACE]: isMspActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "archiveWorkspace"
    ),
    [MspWorkspaceAction.EXTEND_TRIAL]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "extendTrial"
    ),
    [MspWorkspaceAction.START_TRIAL]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "startTrial"
    ),
    [MspWorkspaceAction.VIEW_WORKSPACE]: false,
    [MspWorkspaceAction.VIEW_PROTECTED_USERS]: false,
    [MspWorkspaceAction.VIEW_DEVICES]: false,
    [MspWorkspaceAction.VIEW_TICKETS]: false,
    [MspWorkspaceAction.ASSIGN_GLOBAL_POLICY]: isMspActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "editWorkspace"
    ),
  }) as Record<MspWorkspaceAction, boolean>;

/**
 * Builds disabled state map for workspace actions in global context based on global permissions.
 * @returns Record of actions mapped to disabled flags
 */
export const buildGlobalDisabledMap = (): Record<MspWorkspaceAction, boolean> =>
  ({
    [MspWorkspaceAction.EDIT_MSP_WORKSPACE_LABEL]: isGlobalActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "editWorkspace"
    ),
    [MspWorkspaceAction.EDIT_WORKSPACE]: isGlobalActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "editWorkspace"
    ),
    [MspWorkspaceAction.EDIT_SUBSCRIPTION]: isGlobalActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "editSubscription"
    ),
    [MspWorkspaceAction.CONVERT]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "convertWorkspace"
    ),
    [MspWorkspaceAction.UNARCHIVE_WORKSPACE]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "restoreWorkspace"
    ),
    [MspWorkspaceAction.START_SUBSCRIPTION]: isGlobalActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "startSubscription"
    ),
    [MspWorkspaceAction.STOP_SUBSCRIPTION]: isGlobalActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "stopSubscription"
    ),
    [MspWorkspaceAction.ARCHIVE_WORKSPACE]: isGlobalActionRestricted(
      MspRolePermissionScopes.MSP_PORTAL_SCOPE,
      MspPortalScopeSection.MSP_WORKSPACES,
      "archiveWorkspace"
    ),
    [MspWorkspaceAction.EXTEND_TRIAL]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "extendTrial"
    ),
    [MspWorkspaceAction.START_TRIAL]: isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "startTrial"
    ),
    [MspWorkspaceAction.VIEW_WORKSPACE]: false,
    [MspWorkspaceAction.VIEW_PROTECTED_USERS]: false,
    [MspWorkspaceAction.VIEW_DEVICES]: false,
  }) as Record<MspWorkspaceAction, boolean>;

/**
 * Navigates to the proper route based on the requested workspace action.
 * @param action - Navigation action to perform.
 * @param workspaceId - Target workspace identifier used for routing.
 */
const navigateViewAction = async (action: MspWorkspaceAction, workspaceId: string) => {
  switch (action) {
    case MspWorkspaceAction.VIEW_WORKSPACE:
      await navigateToRoute(workspaceId, RouteName.DASHBOARD_PAGE);
      break;
    case MspWorkspaceAction.VIEW_PROTECTED_USERS:
      await navigateToRoute(workspaceId, RouteName.PROTECTED_USERS);
      break;
    case MspWorkspaceAction.VIEW_DEVICES:
      await navigateToRoute(workspaceId, RouteName.DEVICES);
      break;
    case MspWorkspaceAction.VIEW_TICKETS:
      await navigateToRoute(workspaceId, RouteName.TICKETS);
      break;
  }
};

/**
 * Handles MSP workspace view actions, prompting for MFA when required before navigation.
 * @param action - Workspace action that triggers a navigation.
 * @param item - Workspace item containing metadata such as MFA requirements.
 */
export const handleMspWorkspaceViewAction = async (
  action: MspWorkspaceAction,
  item: BaseMspWorkspaceListItem
): Promise<void> => {
  const myAccountStore = useMyAccountStore();
  const accountStore = useAccountStore();
  const dialogsStore = useDialogsStore();
  const workspaceId = item.workspaceId;

  const launchWithMfa =
    myAccountStore.myAccount.profileData.mfaConfig === MfaStatus.NONE &&
    action === MspWorkspaceAction.VIEW_WORKSPACE &&
    myAccountStore.myAccount.profileData.allowMfa &&
    item.mfaRequired &&
    !accountStore.socialLogin;

  if (launchWithMfa) {
    dialogsStore.openDialog(
      confirmationDialogsConfigConstructor({
        item,
        action: WorkspaceAction.LAUNCH_WITH_2FA,
        callback: () => navigateViewAction(action, workspaceId),
        disable: false,
        disclaimer: "",
      })
    );
    return;
  }

  await navigateViewAction(action, workspaceId);
};
